import type { ExpressionBuilder, Query, Row, Transaction } from "@rocicorp/zero";
import { defineQuery } from "../../zero/transformer";
import { z } from "zod/v4";
import { zql, type Schema } from "../../zero/schema";
import { resourceDefinitions } from "./definitions";
import type * as ResourceDefinitions from "./definitions";
import { createId, accessDenied } from "@corpuscomplete/core/utils";
import type { UserActor } from "@corpuscomplete/core/actor";

/**
 * Access grant boundary for authorization checks across resource scopes.
 *
 * This module is the canonical contract for matching/checking grants by actor,
 * resource type, scope, and role.
 *
 * @see `packages/core/docs/patterns.md`
 */
export namespace AccessGrant {
  export type ResourceType = ResourceDefinitions.ResourceType;
  export type ResourceScope = ResourceDefinitions.ResourceScope;

  export function matching<T extends ResourceType>(
    q: Query<"accessGrant", Schema>,
    actor: UserActor,
    criteria: Criteria<T>,
  ) {
    return q
      .where("resourceType", "=", criteria.type)
      .where("resourceScope", "=", criteria.scope)
      .where("role", "IN", criteria.roles)
      .where((e) => filterForActor(e, actor));
  }

  export type Info = Row<Schema["tables"]["accessGrant"]>;

  export const myGrants = defineQuery(z.undefined(), ({ ctx }) =>
    zql.accessGrant.where((e) => filterForActor(e, ctx.actor)),
  );

  export function any<C extends Criteria<ResourceType>[]>(...criteria: C): C {
    return criteria;
  }

  export type Permissions = Record<string, Criteria<ResourceType>>;

  export type PermissionsResult<T extends Permissions> = { [K in keyof T]: boolean };

  export function definePermissions<T extends Permissions>(permissions: T): T {
    return permissions;
  }

  export function checkPermissions<T extends Permissions>(
    grants: Info[],
    permissions: T,
    resourceIds: ResourceIds<T[keyof T]>,
  ): PermissionsResult<T> {
    const result = {} as PermissionsResult<T>;
    for (const [key, criteria] of Object.entries(permissions)) {
      (result as Record<string, boolean>)[key] = has(
        grants,
        criteria as Criteria<ResourceType>,
        resourceIds as string | Record<string, string>,
      );
    }
    return result;
  }

  function has<C extends Criteria<ResourceType> | readonly Criteria<ResourceType>[]>(
    grants: Info[],
    criteriaOrMany: C,
    resourceIds: ResourceIds<C>,
  ): boolean {
    const criteriaList = Array.isArray(criteriaOrMany)
      ? criteriaOrMany
      : [criteriaOrMany as Criteria<ResourceType>];

    return criteriaList.some((c) => {
      const resourceId =
        typeof resourceIds === "string"
          ? resourceIds
          : (resourceIds as Record<string, string>)[`${c.type}.${c.scope}`];
      if (!resourceId) return false;

      return grants.some(
        (g) =>
          g.resourceType === c.type &&
          g.resourceScope === c.scope &&
          c.roles.includes(g.role as (typeof c.roles)[number]) &&
          g.resourceId === resourceId,
      );
    });
  }

  export async function check<C extends Criteria<ResourceType> | readonly Criteria<ResourceType>[]>(
    tx: Transaction,
    actor: UserActor,
    criteriaOrMany: C,
    resourceIds: ResourceIds<C>,
  ): Promise<boolean> {
    const criteriaList = Array.isArray(criteriaOrMany)
      ? criteriaOrMany
      : [criteriaOrMany as Criteria<ResourceType>];

    const results = await Promise.all(
      criteriaList.map(async (c) => {
        const resourceId =
          typeof resourceIds === "string"
            ? resourceIds
            : (resourceIds as Record<string, string>)[`${c.type}.${c.scope}`];
        if (!resourceId) return false;

        const result = await tx.run(
          matching(zql.accessGrant.where("resourceId", "=", resourceId), actor, c).one(),
        );
        return !!result;
      }),
    );

    return results.some(Boolean);
  }

  export async function require<
    C extends Criteria<ResourceType> | readonly Criteria<ResourceType>[],
  >(
    tx: Transaction,
    actor: UserActor,
    criteriaOrMany: C,
    resourceIds: ResourceIds<C>,
    message = "Access denied",
  ): Promise<void> {
    const allowed = await check(tx, actor, criteriaOrMany, resourceIds);
    if (!allowed) {
      accessDenied(message);
    }
  }

  function filterForActor(eb: ExpressionBuilder<"accessGrant", Schema>, actor: UserActor) {
    return eb.or(
      eb.and(eb.cmp("granteeType", "=", "user"), eb.cmp("granteeId", "=", actor.userId)),
      eb.and(
        eb.cmp("granteeType", "=", "group"),
        eb.exists("granteeGroupMembers", (m) => m.where("userId", "=", actor.userId), {
          flip: true,
        }),
      ),
    );
  }

  type ResourceScopeForType<T extends ResourceType> =
    (typeof resourceDefinitions)[T][number]["scope"];
  type RoleForTypeAndScope<T extends ResourceType, S extends ResourceScopeForType<T>> = Extract<
    (typeof resourceDefinitions)[T][number],
    { scope: S }
  >["allowedRoles"][number];

  export type Criteria<
    T extends ResourceType,
    S extends ResourceScopeForType<T> = ResourceScopeForType<T>,
    R extends RoleForTypeAndScope<T, S> = RoleForTypeAndScope<T, S>,
  > = {
    type: T;
    scope: S;
    roles: R[];
  };
  export function criteria<
    T extends ResourceType,
    S extends ResourceScopeForType<T>,
    R extends RoleForTypeAndScope<T, S>,
  >(type: T, scope: S, roles: R[]): Criteria<T, S, R> {
    return { type, scope, roles };
  }

  export type CriteriaKey<C> = C extends {
    type: infer T extends ResourceType;
    scope: infer S extends string;
  }
    ? `${T}.${S}`
    : never;

  export type CriteriaKeys<C extends readonly Criteria<ResourceType>[]> = CriteriaKey<C[number]>;

  export type ResourceIds<C> =
    C extends Criteria<ResourceType>
      ? string | Record<CriteriaKey<C>, string>
      : C extends readonly Criteria<ResourceType>[]
        ? Record<CriteriaKeys<C>, string>
        : never;

  export type Assignment<
    T extends ResourceType,
    S extends ResourceScopeForType<T> = ResourceScopeForType<T>,
    R extends RoleForTypeAndScope<T, S> = RoleForTypeAndScope<T, S>,
  > = {
    id: string;
    type: T;
    scope: S;
    role: R;
  };
  export function assignment<
    T extends ResourceType,
    S extends ResourceScopeForType<T>,
    R extends RoleForTypeAndScope<T, S>,
  >(id: string, type: T, scope: S, role: R): Assignment<T, S, R> {
    return { id, type, scope, role };
  }

  type Grantee = {
    type: Info["granteeType"];
    id: string;
  };

  // TODO: Should this ever run on the client?
  // If not, could type this with the server transaction type
  export async function add<T extends ResourceType>(
    tx: Transaction,
    assignment: Assignment<T>,
    grantee: Grantee,
    id?: string,
  ) {
    await tx.mutate.accessGrant.insert({
      id: id ?? createId(),
      granteeId: grantee.id,
      granteeType: grantee.type,
      resourceId: assignment.id,
      resourceType: assignment.type,
      resourceScope: assignment.scope,
      role: assignment.role,
    });
  }

  export type Role = "owner" | "editor" | "viewer";

  export function highestRole(grants: readonly { role: string }[]): Role {
    if (grants.some((g) => g.role === "owner")) return "owner";
    if (grants.some((g) => g.role === "editor")) return "editor";
    return "viewer";
  }

  export async function forGrantee(
    tx: Transaction,
    grantee: UserActor,
    resourceId: string,
    type: ResourceType,
    scope: ResourceScope,
  ): Promise<Info | undefined> {
    return tx.run(
      zql.accessGrant
        .where("resourceId", "=", resourceId)
        .where("resourceType", "=", type)
        .where("resourceScope", "=", scope)
        .where("granteeType", "=", "user")
        .where("granteeId", "=", grantee.userId)
        .one(),
    );
  }
}
